/* eslint-disable no-console */
import * as Sentry from '@sentry/nextjs';
import dayjs from 'dayjs';
import * as Bowser from 'bowser';
import { AppUserType, SplunkMessageType } from 'libs/app-enums';
import { AuthUserDetails } from 'hooks/useAuth';

const getUserAgent = () => {
  const userAgent: Bowser.Parser.ParsedResult | null =
    typeof window !== 'undefined'
      ? Bowser.parse(window.navigator.userAgent)
      : null;
  return userAgent;
};

// USER AND DOMAIN INFORMATION
const DOMAIN_ID = 1;

/**
 * Capture an Error/Exception with optional additional information
 *
 * @param {Error} error - A Standard JS error object or an exception-like object.
 * @param {Object | Sentry.CaptureContext} context - A context object (containing contextual information like tags, etc. Similar to Sentry.CaptureContext).
 */
const captureError = async (errorInfo: { error: Error; context?: any }) => {
  const user = AuthUserDetails();
  const isAdvisor = user.isAdvisor || false;
  if (
    process.env.NEXT_PUBLIC_SENTRY_LOGGING_ENABLED &&
    process.env.NEXT_PUBLIC_LOG_CLIENT_ERRORS_TO_SENTRY
  ) {
    Sentry.withScope((scope) => {
      scope.setUser({
        id: user.id,
        username: user.username
      });
      scope.setTag('domainId', DOMAIN_ID);
      scope.setExtra('isAdvisor', isAdvisor);
      return Sentry.captureException(errorInfo.error, errorInfo.context);
    });
  }
};

const logToSplunk = async (
  message: string,
  messageType: SplunkMessageType,
  logData: {
    userId: string;
    userType: AppUserType;
    sessionId: string;
  }
) => {
  const userAgent = getUserAgent();
  const event = {
    Source: 'Web',
    Device: userAgent?.platform.type,
    Message: message,
    Type: messageType.toString(),
    SessionId: logData.sessionId,
    Browser: userAgent?.browser.name,
    'Platform Component': 'ClientSide',
    'Escalation Type':
      messageType === SplunkMessageType.Error ? 'Critical' : 'NonCritical',
    'Client Time': dayjs(new Date().toUTCString()).format(
      'YYYY-MM-DDTHH:mm:ss.SSS'
    ),
    'User ID': logData.userId,
    'User Type': logData.userType.toString(),
    'Full URL':
      typeof window !== 'undefined'
        ? window.location.href.replace(window.location.search, '')
        : null,
    'Query String':
      typeof window !== 'undefined' ? window.location.search : null,
    'User Agent': userAgent ? navigator.userAgent : null,
    'Browser Version': userAgent?.browser.version,
    'Operating System': userAgent?.os.name,
    'Cookie Collection':
      typeof document !== 'undefined'
        ? document.cookie.replace(/SessionId=([^;]+)/g, 'SessionId=xxx')
        : null
  };

  const apiRoot = process.env.NEXT_PUBLIC_API_ROOT;

  fetch(`${apiRoot}/v2/logs`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ event })
  }).catch((reason) => {
    console.log('Error logging to splunk.', reason);
  });
};

/**
 * Capture a message with a log level and optional additional information.
 * 
 * @example 
 * log({
    level: 'info',
    message: 'A test message',
    context: { foo: 'bar', bazz: 'buzz', tags: ['love', 'relationships'] }
  });
 */
const log = async (logInfo: {
  level: 'debug' | 'info' | 'log';
  message: string;
  context?: {
    [x: string]: any;
  };
}) => {
  const user = AuthUserDetails();
  const isAdvisor = user.isAdvisor || false;
  let userType = AppUserType.Unknown;
  if (user.id)
    userType = isAdvisor ? AppUserType.Advisor : AppUserType.Consumer;

  if (
    process.env.NEXT_PUBLIC_SENTRY_LOGGING_ENABLED &&
    process.env.NEXT_PUBLIC_LOG_CLIENT_MESSAGES_TO_SENTRY
  ) {
    Sentry.withScope((scope) => {
      scope.setUser({
        id: user.id,
        username: user.username
      });

      scope.setExtras(logInfo.context);

      return Sentry.captureMessage(logInfo.message, logInfo.level);
    });
  }

  logToSplunk(logInfo.message, SplunkMessageType.Information, {
    userId: user.id,
    userType,
    sessionId: null // TODO: get session id from auth
  });
};

/**
 * Logs a message with optional additional information.
 *
 * @param {string} message - A message to be logged
 * @param {Record<string, unknown>} extraInformation - A context object containing extra/additional context information likek tags, etc. Similar to Sentry.CaptureContext
 *
 * @example
 * const extraInformation = { foo: 'bar', bazz: 'buzz', tags: ['love', 'relationships'] }
 * logInfo('A test message', extraInformation);
 */
export const logInfo = (
  message: string,
  extraInformation?: Record<string, unknown>
) => {
  log({ level: 'info', message, context: extraInformation });
};

/**
 * Logs an error.
 *
 * @param {Error | string } error - An error message as a string or a Standard JS error object or an exception-like object that needs to be captured.
 * @param {Object | Sentry.CaptureContext} extraInformation - A context object (containing additional contextual information like tags, etc. Similar to Sentry.CaptureContext).
 *
 * @example
 * const extraInformation = {};
 * logError('Something bad happened!');
 * logError('An error message with additional context information', extraInformation)
 * logError(new Error('Something bad happened'));
 * logError(new Error('Something bad happened', extraInformation));
 */
export const logError = (
  error: string | Error,
  extraInformation?: Record<string, unknown>
) => {
  const JSErrorObject = typeof error === 'string' ? new Error(error) : error;
  captureError({ error: JSErrorObject, context: extraInformation });
};
